import type {
  EmailCompositionValue,
  EmailFieldErrors,
} from '@kula-ai/components';
import { isHtmlEffectivelyEmpty } from '@kula-ai/components';

const SUBJECT_MAX_LENGTH = 150;

/**
 * Required merge tag on the slot-selection email body — without it,
 * the candidate has no way to reach the self-schedule picker.
 * Mirrors InterviewActivity's self-schedule panel check so edit-mode
 * and create-mode errors read the same.
 */
export const REQUIRED_SELF_SCHEDULE_VARIABLE = 'interview.self_schedule_link';

/** `true` when the content contains `{{interview.self_schedule_link}}`. */
function hasRequiredSelfScheduleLink(content: string): boolean {
  const pattern = new RegExp(
    `{{\\s*${REQUIRED_SELF_SCHEDULE_VARIABLE.replace('.', '\\.')}\\s*}}`
  );
  return pattern.test(content);
}

/** Matches a numeric user id (delegatable sender). The validator
 *  treats anything else — empty string, the `organizer` sentinel —
 *  as unset so the field renders its required error. */
function isValidFromUserId(raw: string): boolean {
  if (!raw) return false;
  const trimmed = raw.trim();
  if (!trimmed) return false;
  const n = Number(trimmed);
  return Number.isFinite(n) && n > 0;
}

export function validateSlotSelectionEmail(
  value: EmailCompositionValue,
  from: string
): EmailFieldErrors | undefined {
  const errors: EmailFieldErrors = {};

  // Accept only a concrete delegatable-user id. The legacy organizer
  // sentinel is filtered out of `fromOptions` for scheduling, so
  // letting it pass here leaves the select visibly empty but reports
  // the form valid — the exact "form is empty in UI but passing
  // validation" report.
  if (!isValidFromUserId(from)) {
    errors.from = 'From is required';
  }

  // Self-schedule only sends to the candidate, but the field is still
  // required — empty means the backend has no recipient to email.
  const toRaw = value.to;
  const toEmpty = Array.isArray(toRaw)
    ? toRaw.filter(Boolean).length === 0
    : !toRaw || !String(toRaw).trim();
  if (toEmpty) {
    errors.to = 'To is required';
  }

  const subject = value.subject?.trim() ?? '';
  if (!subject) {
    errors.subject = 'Subject is required';
  } else if (subject.length > SUBJECT_MAX_LENGTH) {
    errors.subject = `Subject must be at most ${SUBJECT_MAX_LENGTH} characters`;
  }

  const content = value.content ?? '';
  if (isHtmlEffectivelyEmpty(content)) {
    errors.content = 'Body is required';
  } else if (!hasRequiredSelfScheduleLink(content)) {
    // Same shape as the InterviewActivity validator — the step
    // renderer checks `errors.content.includes(...)` to decide
    // whether to show the red "Required variable" hint.
    errors.content = `Body must include {{${REQUIRED_SELF_SCHEDULE_VARIABLE}}}`;
  }

  return Object.keys(errors).length ? errors : undefined;
}
